import { axiosGraphQL } from '../api';
import { GET_USER, REGISTER_USER } from '../graphql/userGraphql';
import { LOGIN_ROUTE, MANAGER_HOME_ROUTE, REGISTRATION_ROUTE, SUPER_ADMIN_CLUBS_ROUTE } from '../routes';
import { USER_ROLES } from '../utility/Enum';

const coreErrorCode = (message) => {
  try { return JSON.parse(message)?.message || message; }
  catch (_) { return message; }
};
const hasGraphQLErrors = (body) => Array.isArray(body?.errors) && body.errors.length > 0;

export class CoreGraphQLError extends Error {
  constructor(body) {
    const code = coreErrorCode(body.errors[0]?.message);
    super(code || 'registration_request_failed');
    this.code = code;
    this.graphQLErrors = body.errors.map((error) => ({ code: coreErrorCode(error?.message), path: error?.path }));
    this.data = body.data;
  }
}

export const requestCoreData = async (query, variables = {}, options = {}) => {
  let response;
  try {
    response = await axiosGraphQL({ query, variables }, options);
  } catch (error) {
    // graphql-http can return request validation errors with HTTP 400.
    if (error?.response?.status === 400 && hasGraphQLErrors(error.response.data)) {
      throw new CoreGraphQLError(error.response.data);
    }
    throw error;
  }
  const body = response?.data;
  if (hasGraphQLErrors(body)) throw new CoreGraphQLError(body);
  if (!body?.data) throw new Error('registration_request_failed');
  return body.data;
};

export const selectSessionClub = (me, preferredClubId) => {
  const clubs = Array.isArray(me?.clubs) ? me.clubs.filter((club) => club?._id) : [];
  if (me?.club?._id && !clubs.some((club) => club._id === me.club._id)) clubs.push(me.club);
  return {
    clubs,
    club: clubs.find((club) => String(club._id) === String(preferredClubId)) ||
      clubs.find((club) => club._id === me?.club?._id) || clubs[0] || null,
  };
};

export const readCachedSessionAccess = (user) => {
  const access = user?.consoleSessionAccess;
  return access?.coreUserId === String(user?._id) &&
    typeof access.hasProfileOrTeam === 'boolean'
    ? { coreUserId: access.coreUserId, hasProfileOrTeam: access.hasProfileOrTeam } : null;
};

export const hasManagerRole = (user) => Boolean(
  user?.roles?.includes(USER_ROLES.CLUB_ADMIN) || user?.roles?.includes(USER_ROLES.SUPER_ADMIN)
);

export const isClublessSuperAdmin = (user) => Boolean(
  user?.roles?.includes(USER_ROLES.SUPER_ADMIN) && !user?.club?._id
);

export const describeConsoleSession = (me, preferredClubId, impersonating = false, cachedAccess) => {
  if (!me?.user?._id) throw new Error('registration_request_failed');
  // Profile/team membership prevents treating an existing non-manager account
  // as a new signup. It never grants Manager access.
  const access = cachedAccess === undefined ? {
    coreUserId: String(me.user._id),
    hasProfileOrTeam: Boolean(me.profiles?.length || me.teams?.length),
  } : cachedAccess;
  const user = { ...me.user, ...selectSessionClub(me, preferredClubId), consoleSessionAccess: access };
  const roles = user.roles || [];
  const superAdmin = roles.includes(USER_ROLES.SUPER_ADMIN);
  const clubAdmin = roles.includes(USER_ROLES.CLUB_ADMIN);
  const hasOtherAccess = roles.some((role) => role !== USER_ROLES.CLUB_ADMIN) ||
    Boolean(access?.hasProfileOrTeam);
  const needsRegistration = !impersonating && !superAdmin && !user.clubs.length &&
    (clubAdmin || (access && !hasOtherAccess));
  const canEnterManager = hasManagerRole(user) && (superAdmin || user.clubs.length > 0);
  return {
    user,
    kind: canEnterManager ? 'established' : needsRegistration ? 'onboarding' : 'restricted',
    destination: canEnterManager
      ? isClublessSuperAdmin(user) ? SUPER_ADMIN_CLUBS_ROUTE : MANAGER_HOME_ROUTE
      : needsRegistration ? REGISTRATION_ROUTE : LOGIN_ROUTE,
  };
};

export const loadConsoleSession = async ({ firebaseUser, storage, request, registerIdentity = true }) => {
  const impersonating = Boolean(storage.getItem('impersonate'));
  // Firebase email verification determines eligibility; Core verifies the token.
  // No names during bootstrap: register also resolves an existing Core User.
  if (registerIdentity && firebaseUser.emailVerified && !impersonating) {
    const data = await request(REGISTER_USER);
    if (!data.register?._id) throw new Error('registration_request_failed');
  }
  // Bootstrap without x-club-id, then validate the saved selection against me.
  // A stale club header must not turn a valid identity into a login failure.
  const data = await request(GET_USER);
  return describeConsoleSession(data.me, storage.getItem('activeClubId'), impersonating);
};

export const hasFirebaseAction = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  return Boolean(params.get('oobCode') && ['signIn', 'verifyAndChangeEmail'].includes(params.get('mode')));
};
